import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "../auth/AuthContext";
import { useLicense } from "../../licensing/LicenseContext";
import { getMyTasks, getMyWorkflowInstances } from "../../api/workflowApi";
import { getFolders } from "../../api/adminApi";
import { FolderTile } from "./FolderChrome";
import PullToRefresh from "../common/PullToRefresh";
import "./homeDashboard.css";

/**
 * The landing summary an ordinary user sees when they log in: who they are, what is waiting on them, what
 * they started, where their content lives — instead of an empty results table.
 *
 * <b>It only ever renders on the IDLE center pane.</b> The moment a query is typed or a folder is opened, the
 * search results view takes the pane back, unchanged. This is deliberately additive: nothing about searching,
 * browsing or the detail pane behaves differently because this exists.
 *
 * "Recently viewed" is deliberately absent: the detail pane on the right already lists it whenever no
 * document is selected, which is precisely when this dashboard is on screen.
 *
 * <b>Every panel here is cheap by construction.</b> The counts come from the workflow tables (small, indexed,
 * per-user) and from the folder list that this pane already loads. Nothing on this screen queries a document
 * table — those have a primary key and nothing else, so a "documents in your folders" tile would be a full
 * scan of hundreds of millions of rows on every login.
 */

const ICONS = {
  inbox: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="22 12 16 12 14 15 10 15 8 12 2 12" /><path d="M5.45 5.11 2 12v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-6l-3.45-6.89A2 2 0 0 0 16.76 4H7.24a2 2 0 0 0-1.79 1.11z" />
    </svg>
  ),
  clock: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" />
    </svg>
  ),
  flow: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="3" width="7" height="5" rx="1" /><rect x="14" y="16" width="7" height="5" rx="1" /><path d="M6.5 8v6a2 2 0 0 0 2 2h5.5" />
    </svg>
  ),
  folder: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" />
    </svg>
  ),
};

function fmtWhen(v, t) {
  if (!v) return "";
  const dt = new Date(v);
  if (isNaN(dt)) return String(v);
  const mins = Math.round((Date.now() - dt.getTime()) / 60000);
  if (mins < 1) return t("home.justNow", { defaultValue: "just now" });
  if (mins < 60) return t("home.minutesAgo", { defaultValue: "{{count}}m ago", count: mins });
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return t("home.hoursAgo", { defaultValue: "{{count}}h ago", count: hrs });
  const days = Math.round(hrs / 24);
  if (days < 30) return t("home.daysAgo", { defaultValue: "{{count}}d ago", count: days });
  return dt.toLocaleDateString();
}

function KpiTile({ icon, value, label, tone, to, onClick }) {
  const body = (
    <>
      <span className={`hd-kpi-icon hd-kpi-icon--${tone || "blue"}`}>{icon}</span>
      <span className="hd-kpi-value">{value}</span>
      <span className="hd-kpi-label">{label}</span>
    </>
  );
  if (to) return <Link className="hd-kpi" to={to}>{body}</Link>;
  if (onClick) return <button type="button" className="hd-kpi" onClick={onClick}>{body}</button>;
  return <div className="hd-kpi">{body}</div>;
}

function Panel({ icon, title, count, action, className, children }) {
  return (
    <section className={`hd-panel${className ? " " + className : ""}`}>
      <header className="hd-panel-head">
        <span className="hd-panel-icon">{icon}</span>
        <h3 className="hd-panel-title">{title}</h3>
        {count != null && <span className="hd-panel-count">{count}</span>}
        {action}
      </header>
      {children}
    </section>
  );
}

function EmptyLine({ text }) {
  return <div className="hd-empty">{text}</div>;
}

/**
 * The idle landing summary — what is waiting on you, what you started, your folders.
 *
 * The summary is split across the two panes of the idle screen, which is why `variant` exists:
 *
 *   "banner" — the greeting and the counters, as a strip across the TOP of the centre pane, with the
 *              workspace directly beneath. The lists are not part of it: a page of cards above the
 *              workspace would push the thing the centre is actually for a screen down.
 *   "panels" — the lists alone (what is waiting on you, what you started, your folders), in the detail
 *              panel, which is otherwise empty whenever no document is selected. Laid out narrow: that
 *              panel is a ~380px column inside a wide viewport, so the stylesheet's media queries,
 *              which key off the VIEWPORT, cannot work it out for themselves.
 *   "full"   — both halves in one column, the original single-pane form.
 *
 * Each variant loads for itself, so either can be mounted alone.
 *
 * `folders` is optional. Left out, the dashboard loads the org folder list itself, so a caller that has
 * no folder list of its own does not have to hold one just to pass it down.
 */
export default function HomeDashboard({ folders, onOpenFolder, workflowEnabled = true, variant = "full" }) {
  const { t } = useTranslation();
  const { token, user } = useAuth();
  // `workflowEnabled` arrives from a caller that asked hasFeature(), which answers optimistically until
  // the licence status lands. Rendering off that optimistic answer is fine; ASKING the server off it is
  // not — at a site without content-governance it put three 403s in the console on every page load. So
  // the fetches wait for the real answer, and only this component's requests are delayed by it.
  const { featuresResolved } = useLicense();
  const canFetchWorkflow = workflowEnabled && featuresResolved;

  const [tasks, setTasks] = useState([]);
  const [instances, setInstances] = useState({ items: [], total: 0, runningTotal: 0 });
  // Two different things used to be one flag, and conflating them cost the summary three of its four
  // counters and its Refresh button on a single failed request:
  //   "absent"  — the server says this surface is not there (not licensed, not permitted). Hide it; the
  //               rest of the summary is still useful and there is nothing to retry.
  //   "failed"  — the request did not get an answer (REST restarting, network, a 500). The counters stay
  //               on screen showing that they are unknown, and Refresh is the way back. Hiding them here
  //               made a momentary blip look like a permanently missing half of the product.
  const [wfState, setWfState] = useState(null);

  // Self-loaded only when the caller did not supply a list.
  const [ownFolders, setOwnFolders] = useState([]);
  const suppliedFolders = Array.isArray(folders);
  useEffect(() => {
    if (suppliedFolders || !token || !canFetchWorkflow) return undefined;
    let alive = true;
    getFolders(token)
      .then((list) => alive && setOwnFolders((Array.isArray(list) ? list : []).filter((f) => f && f.id != null && f.isActive)))
      .catch(() => alive && setOwnFolders([]));
    return () => { alive = false; };
  }, [suppliedFolders, token, canFetchWorkflow]);
  const folderList = suppliedFolders ? folders : ownFolders;

  const load = useCallback(async () => {
    if (!token || !canFetchWorkflow) return;
    try {
      const [myTasks, mine] = await Promise.all([
        getMyTasks(token, false),
        getMyWorkflowInstances(token, { includeCompleted: true, take: 25 }),
      ]);
      setTasks(Array.isArray(myTasks) ? myTasks : []);
      setInstances({
        items: Array.isArray(mine?.items) ? mine.items : [],
        total: Number(mine?.total) || 0,
        runningTotal: Number(mine?.runningTotal) || 0,
      });
      setWfState(null);
    } catch (err) {
      setTasks([]);
      setInstances({ items: [], total: 0, runningTotal: 0 });
      // 403/404/501 is the server telling us the surface is not ours to show. Anything else — no status
      // at all (the request never landed), or a 5xx — is a failure to answer, which is retryable.
      const status = Number(err?.status) || 0;
      setWfState(status === 403 || status === 404 || status === 501 ? "absent" : "failed");
    }
  }, [token, canFetchWorkflow]);

  useEffect(() => { load(); }, [load]);

  // One retry, once, on a retryable failure. The common cause is the API coming back up while the page
  // is loading — without this the summary keeps showing dashes until someone thinks to pull it down.
  const retriedRef = useRef(false);
  useEffect(() => {
    if (wfState !== "failed" || retriedRef.current) return undefined;
    retriedRef.current = true;
    const id = setTimeout(() => load(), 4000);
    return () => clearTimeout(id);
  }, [wfState, load]);

  const overdue = useMemo(() => tasks.filter((x) => x.isOverdue).length, [tasks]);
  const unclaimed = useMemo(() => tasks.filter((x) => x.isPoolTask).length, [tasks]);
  const rootFolders = useMemo(
    () => folderList.filter((f) => (f.parentId ?? null) === null).sort((a, b) => (a.name || "").localeCompare(b.name || "")),
    [folderList]
  );

  const showWorkflow = workflowEnabled && wfState !== "absent";
  // An em dash, not 0: "we do not know" and "nothing is waiting on you" are not the same answer to give
  // someone who is deciding whether they have work to do.
  const wfCount = (n) => (wfState === "failed" ? "—" : n);
  const displayName = user?.fullName || user?.name || user?.userName || user?.email?.split("@")[0] || "";

  const showSummary = variant !== "panels";   // the greeting and the counters
  const showPanels = variant !== "banner";   // the lists

  return (
    /* Drag this down from the top of its pane and let go to reload — there is no Refresh button. Each
       variant is its own pull region, which is also what refreshes it: the two halves live in different
       panes and load separately. Disabled when there is nothing to reload but the folder list. */
    <PullToRefresh
      onRefresh={load}
      disabled={!showWorkflow}
      label={t("home.refresh", { defaultValue: "Refresh" })}
      busyLabel={t("home.refreshing", { defaultValue: "Refreshing…" })}
    >
    <div className={`hd-root${variant === "banner" ? " hd-root--banner" : ""}${variant === "panels" ? " hd-root--compact" : ""}`}>
      {/* The lists have no greeting of their own — the pane says what they are, the way the left pane
          heads itself "Context". Same treatment, same inset, so the two flanks of the screen read as
          a pair. */}
      {variant === "panels" && (
        <div className="hd-panels-title">{t("home.summaryTitle", { defaultValue: "Summary" })}</div>
      )}

      {showSummary && (
      <header className="hd-hero">
        <div>
          <h2 className="hd-hero-title">
            {displayName
              ? t("home.welcomeNamed", { defaultValue: "Welcome back, {{name}}", name: displayName })
              : t("home.welcome", { defaultValue: "Welcome back" })}
          </h2>
          <p className="hd-hero-sub">{t("home.subtitle", { defaultValue: "Your tasks, folders and workflows — search any time from the bar above." })}</p>
        </div>
      </header>
      )}

      {showSummary && (
      <div className="hd-kpis">
        {showWorkflow && (
          <>
            <KpiTile
              icon={ICONS.inbox}
              value={wfCount(tasks.length)}
              label={t("home.kpi.waiting", { defaultValue: "Waiting on you" })}
              tone="blue"
              to="/my-tasks"
            />
            <KpiTile
              icon={ICONS.clock}
              value={wfCount(overdue)}
              label={t("home.kpi.overdue", { defaultValue: "Overdue" })}
              tone={overdue > 0 ? "red" : "grey"}
              to="/my-tasks"
            />
            <KpiTile
              icon={ICONS.flow}
              value={wfCount(instances.runningTotal)}
              label={t("home.kpi.myWorkflowsRunning", { defaultValue: "Workflows running" })}
              tone="violet"
            />
          </>
        )}
        <KpiTile
          icon={ICONS.folder}
          value={folderList.length}
          label={t("home.kpi.folders", { defaultValue: "Folders" })}
          tone="amber"
        />
      </div>
      )}

      {showPanels && (
      <div className="hd-grid">
        {/* Three panels, laid out deliberately: the two work lists sit side by side so their headers
            and rows line up, and folders takes the full width beneath — tiles flow across a wide row
            far better than they stack in a narrow column. "Recently viewed" is NOT repeated here: the
            detail pane already lists it whenever no document is selected, which is exactly this state. */}
        {showWorkflow && (
          <Panel
            icon={ICONS.inbox}
            title={t("home.tasks.title", { defaultValue: "Waiting on you" })}
            count={wfState === "failed" ? null : (tasks.length || null)}
            action={<Link className="hd-panel-link" to="/my-tasks">{t("home.viewAll", { defaultValue: "View all" })}</Link>}
          >
            {tasks.length === 0 ? (
              <EmptyLine text={wfState === "failed"
                ? t("home.loadFailed", { defaultValue: "Could not be loaded. Try Refresh." })
                : t("home.tasks.empty", { defaultValue: "Nothing is waiting on your decision." })} />
            ) : (
              <ul className="hd-list">
                {tasks.slice(0, 5).map((task) => (
                  <li key={task.id}>
                    <Link className="hd-row" to="/my-tasks">
                      <span className="hd-row-main">
                        <span className="hd-row-title">{task.subjectTitle || task.archiveNo || task.name || `#${task.id}`}</span>
                        <span className="hd-row-sub">
                          {task.definitionName ? `${task.definitionName} · ` : ""}{task.name}
                        </span>
                      </span>
                      <span className="hd-row-chips">
                        {task.isOverdue && <span className="hd-chip hd-chip--red">{t("home.chip.overdue", { defaultValue: "Overdue" })}</span>}
                        {task.isPoolTask && <span className="hd-chip hd-chip--grey">{t("home.chip.unclaimed", { defaultValue: "Unclaimed" })}</span>}
                        {task.dueAtUtc && !task.isOverdue && (
                          <span className="hd-row-when">{t("home.due", { defaultValue: "due {{when}}", when: new Date(task.dueAtUtc).toLocaleDateString() })}</span>
                        )}
                      </span>
                    </Link>
                  </li>
                ))}
              </ul>
            )}
            {unclaimed > 0 && (
              <div className="hd-panel-foot">
                {t("home.tasks.poolHint", { defaultValue: "{{count}} offered to a role you belong to — claim one to make it yours.", count: unclaimed })}
              </div>
            )}
          </Panel>
        )}
        {showWorkflow && (
          <Panel
            icon={ICONS.flow}
            title={t("home.workflows.title", { defaultValue: "Workflows you started" })}
            count={wfState === "failed" ? null : (instances.total || null)}
          >
            {instances.items.length === 0 ? (
              <EmptyLine text={wfState === "failed"
                ? t("home.loadFailed", { defaultValue: "Could not be loaded. Try Refresh." })
                : t("home.workflows.empty", { defaultValue: "You have not started a workflow case yet." })} />
            ) : (
              <ul className="hd-list">
                {instances.items.slice(0, 5).map((inst) => (
                  <li key={inst.id}>
                    <div className="hd-row hd-row--static">
                      <span className="hd-row-main">
                        <span className="hd-row-title">{inst.subjectTitle || inst.archiveNo || `#${inst.id}`}</span>
                        <span className="hd-row-sub">
                          {inst.definitionName}
                          {inst.isRunning && inst.waitingOn?.length > 0
                            ? ` · ${t("home.waitingOn", { defaultValue: "waiting on {{step}}", step: inst.waitingOn[0] })}`
                            : ""}
                        </span>
                      </span>
                      <span className="hd-row-chips">
                        {inst.isOverdue && <span className="hd-chip hd-chip--red">{t("home.chip.overdue", { defaultValue: "Overdue" })}</span>}
                        <span className={`hd-chip ${inst.isRunning ? "hd-chip--blue" : "hd-chip--green"}`}>
                          {inst.isRunning
                            ? t("home.chip.running", { defaultValue: "Running" })
                            : (inst.outcome || t("home.chip.finished", { defaultValue: "Finished" }))}
                        </span>
                        <span className="hd-row-when">{fmtWhen(inst.completedAt || inst.startedAt, t)}</span>
                      </span>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </Panel>
        )}
        <Panel
          className="hd-panel--wide"
          icon={ICONS.folder}
          title={t("home.folders.title", { defaultValue: "Your folders" })}
          count={rootFolders.length || null}
        >
          {rootFolders.length === 0 ? (
            <EmptyLine text={t("home.folders.empty", { defaultValue: "No folders have been set up yet." })} />
          ) : (
            <div className="hd-folders">
              {rootFolders.slice(0, 12).map((f) => (
                <FolderTile key={f.id} folder={f} onOpen={() => onOpenFolder && onOpenFolder(f.id)} />
              ))}
            </div>
          )}
        </Panel>
      </div>
      )}
    </div>
    </PullToRefresh>
  );
}
